import { forwardRef } from 'react';
import { styled } from 'styled-components';
import beTwMerge from '@/utils/beTwMerge';
import useRichTextADAFixer from '@/hooks/useRichtTextADAFixer';

const Container = styled.div`
  sup,
  sub {
    vertical-align: baseline !important;
    position: relative;
    top: -0.4em;
  }

  sub {
    top: 0.4em;
  }

  h1,
  h2,
  h3,
  h4,
  h5,
  h6 {
    margin-top: 0;
  }

  & li,
  & ul,
  & ol,
  & a {
    @media (max-width: 767px) {
      font-size: 14px;
    }

    @media (min-width: 768px) {
      font-size: 16px;
    }
  }
`;

interface IRichText {
  content: string;
  className?: string;
  deep?: number;
  isOnlyPcTextWhite?: boolean;
  isTextWhite?: boolean;
}

function RichText({ content, className, deep = 1, isOnlyPcTextWhite = true, isTextWhite = false }: IRichText, ref) {
  const pStyles = '[&_p]:tw-mb-0 [&_p]:tw-font-book [&_p]:tw-text-be-sm [&_p]:md:tw-text-be-base';
  const aStyles = '[&>p>a]:tw-underline';
  const h1Styles =
    '[&_h1]:tw-font-ebgaramond [&_h1]:tw-normal-case [&_h1]:tw-font-normal [&_h1]:tw-text-garamond-extralarge-mobile [&_h1]:desktop:tw-text-garamond-extralarge-desktop [&_h1]:tw-mb-be-2';
  const h2Styles =
    '[&_h2]:tw-font-ebgaramond [&_h2]:tw-normal-case [&_h2]:tw-font-normal [&_h2]:tw-text-garamond-large-mobile [&_h2]:desktop:tw-text-garamond-large-desktop [&_h2]:tw-mb-be-2';
  const h3Styles =
    '[&_h3]:tw-font-ebgaramond [&_h3]:tw-normal-case [&_h3]:tw-font-normal [&_h3]:tw-text-garamond-medium-mobile [&_h3]:desktop:tw-text-garamond-medium-desktop [&_h3]:tw-mt-0 [&_h3]:tw-mb-be-2';
  const h4Styles =
    '[&_h4]:tw-font-[Avenir_Medium] [&_h4]:tw-text-be-base [&_h4]:desktop:tw-text-be-lg [&_h4]:tw-normal-case [&_h4]:tw-mb-be-2';
  const h5Styles = '[&>h5]:tw-font-[Avenir] [&>h5]:tw-normal-case [&>h5]:tw-mb-be-2';
  const h6Styles = '[&>h6]:tw-font-[Avenir] [&>h6]:tw-normal-case [&>h6]:tw-mb-be-2';
  const strongStyles = '[&_strong]:tw-font-heavy [&_strong]:tw-font-bold [&_b]:tw-font-heavy [&_b]:tw-font-bold';

  // Could we have this code commented for when they need to be added?
  // const h4Styles = '[&>h4]:tw-font-[Avenir] [&>h4]:tw-text-be-sm [&>h4]:tablet:tw-text-be-base [&>h4]:desktop:tw-text-be-lg [&>h4]:tw-mb-be-2';
  // const h5Styles = '[&>h5]:tw-font-[Avenir] [&>h5]:tw-text-be-xs [&>h5]:tablet:tw-text-be-sm [&>h5]:desktop:tw-text-be-base [&>h5]:tw-mb-be-2';
  // const h6Styles = '[&>h6]:tw-font-[Avenir] [&>h6]:tw-text-be-sm [&>h6]:tablet:tw-text-be-base [&>h6]:desktop:tw-text-be-lg [&>h6]:tw-mb-be-2';

  const modifiedHtml = useRichTextADAFixer(content, deep, isOnlyPcTextWhite, isTextWhite);

  return (
    <Container ref={ref}>
      <div
        className={beTwMerge(
          'tw-text-be-dark-gray tw-font-book tw-text-be-sm md:tw-text-be-base',
          pStyles,
          aStyles,
          h1Styles,
          h2Styles,
          h3Styles,
          h4Styles,
          h5Styles,
          h6Styles,
          strongStyles,
          className
        )}
        dangerouslySetInnerHTML={{ __html: modifiedHtml }}
      />
    </Container>
  );
}

export default forwardRef(RichText);
